import { X } from 'lucide-react'
import { useCallback, useEffect, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

import { useT } from '../lib/i18n'
import { useOverlayRoot } from './OverlayContext'

interface SheetProps {
  open: boolean
  onClose: () => void
  title: string
  children: ReactNode
  footer?: ReactNode
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

/**
 * A bottom sheet, rendered into `#overlay-root` — **inside the phone column**.
 *
 * This is the component rule 2 exists for. At body level the scrim covers a
 * 1920 px page and darkens the desktop instead of the 430 px column; SUAP-0104
 * proved the containment with a hand-built div and left this as the first real
 * consumer. Nothing here is `position: fixed`: the overlay root is
 * `absolute inset-0` within the column, and the scrim is `absolute inset-0`
 * within that.
 *
 * Behaviour that makes it a dialog rather than a div:
 *
 * - focus moves into the sheet on open and **returns to the opener** on close
 * - Tab cycles inside; Escape closes
 * - the scrim closes on click, the panel does not (clicks stop at the panel)
 * - the scroller behind is frozen while it is open, so a drag on the scrim does
 *   not scroll the page underneath
 *
 * **It does not animate.** An earlier version carried
 * `motion-safe:transition-transform` and a docstring promising a slide-up, but no
 * transform ever changed, so the class was decoration and the sentence was a
 * claim. A review measured `transform: none` in both motion modes. Removed rather
 * than implemented: a sheet that appears is honest, and the animation can be
 * added deliberately when someone wants it — with a `@theme` keyframe and a
 * `check:kit` row that proves it respects `prefers-reduced-motion`.
 *
 * SUAP-0103.
 */
export function Sheet({ open, onClose, title, children, footer }: SheetProps) {
  const { t } = useT()
  const overlayRoot = useOverlayRoot()
  const panelRef = useRef<HTMLDivElement | null>(null)
  const openerRef = useRef<Element | null>(null)

  // `onClose` is a fresh closure on every parent render — which is how anyone
  // normally writes it. Holding it in a ref keeps the key handler stable, so the
  // open effect below depends on `open` alone.
  //
  // The first version did not, and the bug it caused is worth keeping written
  // down: any state change in the parent re-ran the effect, which unconditionally
  // focused the first focusable (the close button) and re-captured `openerRef`
  // from whatever was focused at that instant. Pressing ArrowUp on a Stepper
  // inside the sheet ejected focus to Tutup, and Escape afterwards returned focus
  // to nothing. Found in review; `check:kit`'s Tab cycle could not see it because
  // tabbing changes no state.
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!open) return
      if (event.key === 'Escape') {
        event.stopPropagation()
        onCloseRef.current()
        return
      }
      if (event.key !== 'Tab') return

      const panel = panelRef.current
      if (panel === null) return
      const focusable = [...panel.querySelectorAll<HTMLElement>(FOCUSABLE)]
      if (focusable.length === 0) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (first === undefined || last === undefined) return

      // Wrap by hand: without this, Tab walks out of the sheet and into the
      // screen behind it, which is still rendered and still clickable.
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    },
    [open],
  )

  useEffect(() => {
    if (!open) return undefined

    openerRef.current = document.activeElement
    const panel = panelRef.current
    const target = panel?.querySelector<HTMLElement>(FOCUSABLE) ?? panel
    target?.focus()

    document.addEventListener('keydown', onKeyDown, true)

    // Freeze the column's scroller, not the document: the document does not
    // scroll in this app (PhoneFrame is overflow-hidden), <main> does.
    const scroller = overlayRoot?.closest('[data-phone-frame]')?.querySelector('main') ?? null
    const previousOverflow = scroller?.style.overflow ?? null
    if (scroller !== null) scroller.style.overflow = 'hidden'

    return () => {
      document.removeEventListener('keydown', onKeyDown, true)
      if (scroller !== null) scroller.style.overflow = previousOverflow ?? ''
      // Returning focus is the half everyone forgets: without it, the next Tab
      // starts from the top of the document.
      if (openerRef.current instanceof HTMLElement) openerRef.current.focus()
    }
    // `open` and the portal target only. `onKeyDown` is stable by construction
    // (it reads onClose through a ref), and adding anything render-dependent here
    // re-runs the focus grab on every keystroke — see the note above.
  }, [onKeyDown, open, overlayRoot])

  if (!open || overlayRoot === null) return null

  return createPortal(
    <div className="absolute inset-0 flex flex-col justify-end">
      {/* The scrim. A button, because it is a click target with a job, and a
          div with onClick is invisible to a keyboard — Escape is the keyboard
          path, and this stays out of the tab order deliberately. */}
      <button
        type="button"
        tabIndex={-1}
        aria-hidden
        onClick={onClose}
        className="absolute inset-0 bg-scrim/55"
      />

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className="relative max-h-[85%] overflow-y-auto rounded-t-sheet border-t border-hairline bg-surface outline-none"
        style={{ paddingBottom: 'var(--suap-safe-b)' }}
      >
        <div className="flex items-center justify-between gap-3 border-b border-hairline px-4 py-3">
          <h2 className="min-w-0 truncate text-base font-semibold text-ink">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('action.close')}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-chip text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink"
          >
            <X aria-hidden size={20} />
          </button>
        </div>

        <div className="p-4">{children}</div>

        {footer === undefined ? null : (
          <div className="border-t border-hairline p-4">{footer}</div>
        )}
      </div>
    </div>,
    overlayRoot,
  )
}
